import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterSearch } from '@/components/common/filters/FilterSearch'
import { FilterDateRangePicker } from '@/components/common/filters/FilterDateRangePicker'
import { TableCard } from '@/components/common/table/TableCard'
import { PaginationFooter } from '@/components/common/table/PaginationFooter'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { notify } from '@/lib/notify'
import {
  useGetApiCompaniesCurrentNotifications,
  useDeleteApiCompaniesCurrentNotificationsId,
  getGetApiCompaniesCurrentNotificationsQueryKey,
} from '@/api/generated/company-management-user/company-management-user'
import type { GenericInAppNotificationDefinitionResponse } from '@/api/model/genericInAppNotificationDefinitionResponse'
import { NotificationsDataTable } from './NotificationsDataTable'
import type { GetApiCompaniesCurrentNotificationsParams } from '@/api/model'

const DEFAULT_PAGE_SIZE = 10

interface Props {
  onEdit: (item: GenericInAppNotificationDefinitionResponse) => void
  onDetail: (item: GenericInAppNotificationDefinitionResponse) => void
}

export function NotificationsTable({ onEdit, onDetail }: Props) {
  const { t } = useTranslation(['notifications', 'filters', 'common'])
  const queryClient = useQueryClient()
  const [confirmDelete, setConfirmDelete] = useState<GenericInAppNotificationDefinitionResponse | null>(null)
  const [queryParams, setQueryParams] = useState<GetApiCompaniesCurrentNotificationsParams>({
    Offset: 0,
    Limit: DEFAULT_PAGE_SIZE,
  })

  const { data: response, isLoading, isError } = useGetApiCompaniesCurrentNotifications(queryParams)

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentNotificationsQueryKey() })

  const { mutate: deleteNotification, isPending: isDeleting } = useDeleteApiCompaniesCurrentNotificationsId({
    mutation: {
      onSuccess: () => {
        notify.success(t('toasts.deleted'))
        setConfirmDelete(null)
        invalidate()
      },
      onError: (error) => notify.apiError(error, t('toasts.deleteError')),
    },
  })

  return (
    <>
      <FilterRow>
        <FilterSearch
          searchLabel={t('filters:searchbar.notificationsPage')}
          search={queryParams.Search}
          onChange={(value) => setQueryParams(prev => ({ ...prev, Search: value, Offset: 0 }))}
        />
        <FilterDateRangePicker
          from={queryParams.CreatedFrom}
          to={queryParams.CreatedTo}
          onChange={({ from, to }) => setQueryParams(prev => ({ ...prev, CreatedFrom: from, CreatedTo: to, Offset: 0 }))}
        />
      </FilterRow>

      <TableCard>
        <div className="flex-1 overflow-auto scrollbar-custom relative">
          <NotificationsDataTable
            isLoading={isLoading}
            isError={isError}
            items={response?.data?.items}
            onEdit={onEdit}
            onDetail={onDetail}
            onDelete={setConfirmDelete}
          />
        </div>

        <PaginationFooter
          offset={queryParams.Offset}
          limit={queryParams.Limit}
          totalCount={response?.data?.totalCount}
          onOffsetChange={(offset) => setQueryParams(prev => ({ ...prev, Offset: offset }))}
          onPageSizeChange={(size) => setQueryParams(prev => ({ ...prev, Limit: size, Offset: 0 }))}
        />
      </TableCard>

      <ConfirmDialog
        open={!!confirmDelete}
        title={t('list.delete.title')}
        description={t('list.delete.description', { name: confirmDelete?.title })}
        confirmLabel={t('common:buttons.delete')}
        confirmButtonVariant="destructive"
        isLoading={isDeleting}
        onConfirm={() => confirmDelete?.id && deleteNotification({ id: confirmDelete.id })}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  )
}
